/**
 * Trip Generation Service for Agent
 * 
 * Reuses the trip generation logic from GenerateRidesDrawer
 * to generate trips based on agent answers
 */

import { v4 as uuidv4 } from 'uuid';
import { Point, Line, Polygon, Layer } from '../../context/context';
import { weightedRandom, randomPointInPolygon, makePoint, weightedRandomTime } from '../../components/map/components/generate-rides/utils';
import { Peak } from '../../components/map/components/generate-rides/types';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { combineDateWithTime, formatDateAsWallTime } from '../../utils/dateTimeUtils';
import { lineAddressPlaceholders } from '../../utils/rides/addressPlaceholders';

export interface TripGenerationParams {
  numberOfRides: number;
  startDate: string; // Format: "YYYY-MM-DD HH:MM:SS"
  endDate: string; // Format: "YYYY-MM-DD HH:MM:SS"
  mode: 'Point to Point' | 'Point to Hub' | 'Hub to Hub';
  geofencePolygon?: Polygon;
  hubs?: Array<{ id: string; name: string; coordinates: [number, number]; percentage: number }>;
  arrivalFixedProbability?: number; // 0-100
  peaks?: Peak[];
  rideTypes?: Array<{ type: string; probability: number }>;
  passengerTypes?: Array<{ passenger_type: string; processing_time?: number; probability: number }>;
  numberOfPassengers?: Array<{ number_of_passengers: number; probability: number }>;
  hubPopularity?: Array<{ name: string; probability: number }>; // For Point to Hub mode
  useMeetingPointsOnly?: boolean;
  meetingPoints?: Point[];
}

export interface GeneratedTrips {
  points: Point[];
  lines: Line[];
  layer: Layer;
}

/**
 * Generate trips based on agent answers
 */
export function generateTripsFromAgentAnswers(
  params: TripGenerationParams,
  existingLayers: Layer[]
): GeneratedTrips {
  const {
    numberOfRides,
    startDate,
    endDate,
    mode,
    geofencePolygon,
    hubs = [],
    arrivalFixedProbability = 50,
    peaks = [],
    rideTypes = [
      { type: 'departure_fixed', probability: 1 - (arrivalFixedProbability / 100) },
      { type: 'arrival_fixed', probability: arrivalFixedProbability / 100 },
    ],
    passengerTypes = [
      { passenger_type: 'standard', processing_time: 30, probability: 0.98 },
      { passenger_type: 'wheelchair', processing_time: 90, probability: 0.02 },
    ],
    numberOfPassengers = [
      { number_of_passengers: 1, probability: 0.95 },
      { number_of_passengers: 2, probability: 0.025 },
      { number_of_passengers: 3, probability: 0.025 },
    ],
    hubPopularity = [],
    useMeetingPointsOnly = false,
    meetingPoints = [],
  } = params;

  // Extract date and time from date strings (format: "YYYY-MM-DD HH:MM:SS")
  const startDatePart = startDate.split(' ')[0] || DEFAULT_SIMULATION_DATE;
  const startTimeStr = startDate.split(' ')[1]?.substring(0, 5) || '06:00';
  const endDatePart = endDate.split(' ')[0] || DEFAULT_SIMULATION_DATE;
  const endTimeStr = endDate.split(' ')[1]?.substring(0, 5) || '22:00';
  
  // Create date objects using the actual dates from startDate and endDate
  const startDateObj = new Date(combineDateWithTime(startDatePart, startTimeStr));
  const endDateObj = new Date(combineDateWithTime(endDatePart, endTimeStr));
  
  console.log('📅 Trip generation using dates:', {
    startDate: startDate,
    endDate: endDate,
    startDateObj: startDateObj.toISOString(),
    endDateObj: endDateObj.toISOString(),
    durationHours: (endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60),
  });

  const points: Point[] = [];
  const lines: Line[] = [];

  // Determine ride pattern based on mode and hubs
  let ridePattern: { p2p: number; p2h: number; h2h: number };
  if (mode === 'Point to Point') {
    ridePattern = { p2p: 100, p2h: 0, h2h: 0 };
  } else if (mode === 'Point to Hub') {
    ridePattern = { p2p: 0, p2h: 100, h2h: 0 };
  } else if (mode === 'Hub to Hub') {
    ridePattern = { p2p: 0, p2h: 0, h2h: 100 };
  } else {
    // Default pattern
    ridePattern = { p2p: 50, p2h: 30, h2h: 20 };
  }

  // Convert hubPopularity to hubWeights format if needed
  const hubWeights = hubPopularity.length > 0
    ? hubPopularity.map(hub => {
        const hubPoint = hubs.find(h => h.name === hub.name);
        if (hubPoint) {
          return {
            ...hubPoint,
            percentage: hub.probability * 100,
          };
        }
        return null;
      }).filter((h): h is NonNullable<typeof h> => h !== null)
    : hubs;

  // Get polygon coordinates if available
  const polygonCoords: [number, number][] | null = geofencePolygon
    ? geofencePolygon.coordinates[0]
    : null;

  // Helper function to pick pattern
  const pickPattern = () => {
    const rand = Math.random() * 100;
    if (rand < ridePattern.p2p) return 'p2p';
    if (rand < ridePattern.p2p + ridePattern.p2h) return 'p2h';
    return 'h2h';
  };

  const getRandomMeetingPoint = (): Point | null => {
    if (meetingPoints.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * meetingPoints.length);
    return meetingPoints[randomIndex];
  };

  // Generate trips
  for (let i = 0; i < numberOfRides; i++) {
    let origin: Point, destination: Point;
    const pattern = pickPattern();

    // Determine passenger types
    const numPassengers = weightedRandom(
      numberOfPassengers,
      numberOfPassengers.map(g => g.probability)
    ).number_of_passengers;
    
    let standardSeat = 0;
    let wheelchair = 0;
    
    for (let j = 0; j < numPassengers; j++) {
      const type = weightedRandom(
        passengerTypes,
        passengerTypes.map(p => p.probability)
      ).passenger_type.toLowerCase();
      
      if (type.includes('wheelchair')) {
        wheelchair++;
      } else {
        standardSeat++;
      }
    }

    const hasWheelchairPassenger = wheelchair > 0;

    const getOriginDestination = (isOrigin: boolean, isWheelchairPassenger: boolean = false): Point => {
      if (useMeetingPointsOnly && !isWheelchairPassenger) {
        const meetingPoint = getRandomMeetingPoint();
        if (meetingPoint) {
          return makePoint(
            meetingPoint.coordinates,
            `${isOrigin ? 'Origin' : 'Destination'} ${i + 1}`,
            isOrigin ? '#FF0000' : '#00FF00'
          );
        }
      }

      if (!polygonCoords) {
        throw new Error('Meeting-point trip generation requires a geofence polygon when no meeting point is available');
      }

      return makePoint(
        randomPointInPolygon(polygonCoords),
        `${isOrigin ? 'Origin' : 'Destination'} ${i + 1}`,
        isOrigin ? '#FF0000' : '#00FF00'
      );
    };

    // Determine origin and destination based on pattern
    if (pattern === 'h2h' && hubWeights.length > 1) {
      // Hub to Hub: pick two different hubs
      let hub1: typeof hubWeights[0];
      let hub2: typeof hubWeights[0];
      do {
        hub1 = weightedRandom(hubWeights, hubWeights.map(h => h.percentage / 100));
        hub2 = weightedRandom(hubWeights, hubWeights.map(h => h.percentage / 100));
      } while (hub1.id === hub2.id && hubWeights.length > 1);
      
      origin = makePoint(hub1.coordinates, `Origin ${i + 1}`, '#FF0000');
      destination = makePoint(hub2.coordinates, `Destination ${i + 1}`, '#00FF00');
    } else if (pattern === 'p2h' && hubWeights.length > 0) {
      // Point to Hub: random point to hub or hub to random point
      const hubWeight = weightedRandom(hubWeights, hubWeights.map(h => h.percentage / 100));
      
      if (polygonCoords) {
        if (Math.random() < 0.5) {
          // Origin is random point or meeting point, destination is hub
          origin = getOriginDestination(true, hasWheelchairPassenger);
          destination = makePoint(hubWeight.coordinates, `Destination ${i + 1}`, '#00FF00');
        } else {
          // Origin is hub, destination is random point or meeting point
          origin = makePoint(hubWeight.coordinates, `Origin ${i + 1}`, '#FF0000');
          destination = getOriginDestination(false, hasWheelchairPassenger);
        }
      } else {
        // No polygon, use hub coordinates for both
        origin = makePoint(hubWeight.coordinates, `Origin ${i + 1}`, '#FF0000');
        destination = makePoint(hubWeight.coordinates, `Destination ${i + 1}`, '#00FF00');
      }
    } else {
      // Point to Point: both random points or meeting points
      origin = getOriginDestination(true, hasWheelchairPassenger);
      destination = getOriginDestination(false, hasWheelchairPassenger);
    }

    // Generate request time
    const requestTime = weightedRandomTime(startDateObj, endDateObj, peaks);
    
    // Get ride type
    const rideType = weightedRandom(rideTypes, rideTypes.map(r => r.probability)).type;

    // Assign unique IDs and set layer properties
    origin.id = uuidv4();
    destination.id = uuidv4();
    origin.hidden = false;
    destination.hidden = false;
    points.push(origin, destination);

    const requestTimeString = requestTime instanceof Date
      ? formatDateAsWallTime(requestTime)
      : requestTime;

    // Create line (trip)
    const rideId = `Trip-${i + 1}`;
    const userId = `user-${i + 1}`;
    
    const line: Line & { originPointId: string; destPointId: string } = {
      id: uuidv4(),
      type: 'line',
      name: rideId,
      ride_id: rideId,
      user_id: userId,
      colour: '#0000FF',
      description: '',
      opacity: '1',
      borderColour: '#0000FF',
      borderWidth: 2,
      isLocked: false,
      coordinates: [origin.coordinates, destination.coordinates],
      arrowStyle: 'arrow',
      time: requestTimeString,
      numPassengers,
      standard_seat: standardSeat,
      wheelchair: wheelchair,
      request_type: rideType,
      originPointId: origin.id,
      destPointId: destination.id,
      hidden: false,
      ...lineAddressPlaceholders({}, i + 1),
    };
    
    lines.push(line);
  }

  // Create new layer
  // Ensure we get a unique layer ID that doesn't conflict with existing layers
  const existingLayerIds = existingLayers
    .map(l => l.layerId)
    .filter((id): id is number => id !== undefined && id !== null);
  
  const newLayerId = existingLayerIds.length > 0
    ? Math.max(...existingLayerIds) + 1
    : existingLayers.length;
  
  console.log('Creating new layer with ID:', newLayerId);
  console.log('Points to add:', points.length);
  console.log('Lines to add:', lines.length);
  console.log('Existing layers:', existingLayers.length);
  console.log('Existing layer IDs:', existingLayerIds);
  
  // Set layerId on all shapes
  points.forEach(p => {
    p.layerId = newLayerId;
  });
  lines.forEach(l => {
    l.layerId = newLayerId;
  });
  
  const newLayer: Layer = {
    layerId: newLayerId,
    layerName: `Agent Generated Trips ${newLayerId + 1}`,
    hidden: false,
    shapes: [...points, ...lines],
  };
  
  console.log('Created layer:', newLayer);
  console.log('Layer shapes count:', newLayer.shapes.length);
  console.log('Layer hidden:', newLayer.hidden);

  return {
    points,
    lines,
    layer: newLayer,
  };
}
